'use client';

import styled from 'styled-components';
import { ReactNode } from 'react';

interface CardProps {
  children: ReactNode;
  padding?: 'sm' | 'md' | 'lg';
  shadow?: 'sm' | 'md' | 'lg' | 'xl';
  border?: boolean;
  hover?: boolean;
  className?: string;
}

const StyledCard = styled.div<{
  $padding: string;
  $shadow: string;
  $border: boolean;
  $hover: boolean;
}>`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: ${({ $border, theme }) => 
    $border ? `1px solid ${theme.colors.gray[200]}` : 'none'};
  box-shadow: ${({ $shadow, theme }) => theme.shadows[$shadow as keyof typeof theme.shadows]};
  transition: all 0.2s ease;
  overflow: hidden;

  ${({ $padding, theme }) => {
    const paddingMap = {
      sm: theme.spacing.md,
      md: theme.spacing.lg,
      lg: theme.spacing.xl,
    };
    return `padding: ${paddingMap[$padding as keyof typeof paddingMap]};`;
  }}

  ${({ $hover, theme }) =>
    $hover &&
    `
    cursor: pointer;
    
    &:hover {
      box-shadow: ${theme.shadows.lg};
      transform: translateY(-2px);
    }
  `}
`;

const CardHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.gray[900]};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const CardDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray[600]};
  line-height: 1.5;
`;

const CardContent = styled.div`
  color: ${({ theme }) => theme.colors.gray[800]};
`;

const CardFooter = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.gray[200]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

type CardComponent = React.FC<CardProps> & {
  Header: typeof CardHeader;
  Title: typeof CardTitle;
  Description: typeof CardDescription;
  Content: typeof CardContent;
  Footer: typeof CardFooter;
};

export const Card: CardComponent = Object.assign(
  ({
    children,
    padding = 'md',
    shadow = 'sm',
    border = true,
    hover = false,
    className,
  }: CardProps) => {
    return (
      <StyledCard
        $padding={padding}
        $shadow={shadow}
        $border={border}
        $hover={hover}
        className={className}
      >
        {children}
      </StyledCard>
    );
  },
  {
    Header: CardHeader,
    Title: CardTitle,
    Description: CardDescription,
    Content: CardContent,
    Footer: CardFooter,
  }
);
